import React from 'react';

import pickBy from 'lodash/pickBy';
import {WithRouterProps} from 'react-router';

export interface ConfirmOnLeaveProps {
  shouldConfirmOnLeave: (identifier: string, enabled: boolean) => void;
  withLeaveConfirmationDisabled: (fn: () => unknown) => void;
  clearConfirmations: () => void;
}

type RouterProps = Pick<WithRouterProps, 'router' | 'routes'>;

type State = {
  confirmIdentifiers: Record<string, boolean>;
};

export function confirmOnLeaveEnhancer(
  confirmMessageGetter: () => string | void
) {
  return function <P extends RouterProps & ConfirmOnLeaveProps>(
    WrappedComponent: React.ComponentType<P>
  ): React.ComponentType<Omit<P, keyof ConfirmOnLeaveProps>> {
    class EnhancedComponent extends React.PureComponent<P, State> {
      enabled = true;
      state = {
        confirmIdentifiers: {},
      };

      componentDidMount() {
        // register hook to catch navigation away.
        this.props.router.setRouteLeaveHook(
          [...this.props.routes].pop(),
          this.getConfirmMessage
        );
        window.onbeforeunload = this.pageCloseHandler;
      }

      componentWillUnmount() {
        window.onbeforeunload = null;
      }

      pageCloseHandler = (e) => {
        if (!this.enabled) {
          return;
        }

        const message = this.getConfirmMessage();

        if (message) {
          e.returnValue = message;
          return message;
        }
      };
      getConfirmMessage = () => {
        if (!this.enabled) {
          return;
        }

        const enabledIdentifiers = Object.keys(
          pickBy(this.state.confirmIdentifiers)
        );

        if (enabledIdentifiers.length) {
          return confirmMessageGetter();
        }
      };
      shouldConfirm = (identifier: string, value: boolean) => {
        this.setState({
          confirmIdentifiers: {
            ...this.state.confirmIdentifiers,
            [identifier]: value,
          },
        });
      };
      withLeaveConfirmationDisabled = (fn: () => any) => {
        this.enabled = false;
        fn();
        this.enabled = true;
      };

      clearConfirmations = () => {
        this.setState({confirmIdentifiers: {}});
      };

      render() {
        return (
          <WrappedComponent
            {...this.props}
            clearConfirmations={this.clearConfirmations}
            shouldConfirmOnLeave={this.shouldConfirm}
            withLeaveConfirmationDisabled={this.withLeaveConfirmationDisabled}
          />
        );
      }
    }

    return EnhancedComponent;
  };
}
